// SEO landing page for an individual agent category.
//
// Today the in-app catalog (/agents) drives category clicks through a
// query-string filter (`/agents?category=<slug>`). That keeps the
// authenticated experience working but gives crawlers a single URL with
// nothing category-specific in the prerendered HTML. This route fixes that:
//
//   /agents/category/<slug>
//
// is statically pre-rendered for every enabled category, with its own
// title/description/canonical/JSON-LD, and re-uses the existing
// `AgentsPage` (filtered by category) so the in-app behaviour is identical.
// The query-string form keeps working unchanged.
//
// Mirrors the SEO pattern used by /tools/[slug], /blog/[slug],
// /tasks/template/[slug], and /explore/category/[slug].
import React, { useMemo } from 'react';
import { useLocalSearchParams } from 'expo-router';
import MetaHead from '@/components/MetaHead';
import { agentApiSsr } from '@/lib/agentApi';
import { AgentsPage } from '@/app/(app)/agents/index';

// ---------------------------------------------------------------------------
// SEO — static generation
// ---------------------------------------------------------------------------
// Module-level cache populated by `generateStaticParams` so the build-time
// HTML render has access to the same data the runtime render will. Keyed by
// slug; each entry holds `{ category, agents }`.
const _staticCategoryCache = {};

export async function generateStaticParams() {
  // Rendered on demand (client SPA), not prerendered. Returning [] keeps
  // `expo export` fast and avoids hammering the API at build time.
  return [];
}

// ---------------------------------------------------------------------------
// MetaHead helpers
// ---------------------------------------------------------------------------
const SITE_URL = 'https://gab.ai';

const buildCategoryTitle = (cat) => {
  if (cat?.meta?.title) return cat.meta.title;
  if (cat?.name) return `${cat.name} AI Bots — Gab AI`;
  return 'AI Bots Category';
};

const buildCategoryDescription = (cat) => {
  if (cat?.meta?.description) return cat.meta.description;
  if (cat?.description) return cat.description;
  if (cat?.name) {
    return `Browse specialized AI bots for ${cat.name.toLowerCase()} on Gab AI. Pick a bot and start a conversation.`;
  }
  return 'Browse Gab AI’s catalog of specialized AI bots. Pick a bot and start a conversation.';
};

const buildKeywords = (cat) => {
  if (cat?.meta?.keywords) return cat.meta.keywords;
  const base = ['AI bots', 'AI assistants', 'specialized AI', 'Gab AI bots'];
  if (cat?.name) base.unshift(cat.name, `${cat.name} AI`, `AI ${cat.name}`);
  return base.join(', ');
};

const buildStructuredData = (cat, agents, slug) => {
  const url = `${SITE_URL}/agents/category/${slug}`;
  const itemList = (agents || []).slice(0, 30).map((agent, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    url: `${SITE_URL}/g/${agent.id}`,
    name: agent.name,
    ...(agent.avatarPath ? { image: agent.avatarPath } : {}),
  }));

  const collection = {
    '@type': 'CollectionPage',
    name: buildCategoryTitle(cat),
    description: buildCategoryDescription(cat),
    url,
    isPartOf: { '@type': 'WebSite', name: 'Gab AI', url: SITE_URL },
    inLanguage: 'en-US',
    ...(cat?.stats?.agentCount
      ? {
          mainEntity: {
            '@type': 'ItemList',
            numberOfItems: cat.stats.agentCount,
            ...(itemList.length ? { itemListElement: itemList } : {}),
          },
        }
      : itemList.length
      ? {
          mainEntity: {
            '@type': 'ItemList',
            numberOfItems: itemList.length,
            itemListElement: itemList,
          },
        }
      : {}),
  };

  const breadcrumbs = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'AI Bots', item: `${SITE_URL}/agents` },
      {
        '@type': 'ListItem',
        position: 3,
        name: cat?.name || slug,
        item: url,
      },
    ],
  };

  return { '@context': 'https://schema.org', '@graph': [collection, breadcrumbs] };
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function AgentCategoryRoute() {
  const params = useLocalSearchParams();

  const { slug, seo } = useMemo(() => {
    const s =
      typeof params?.slug === 'string'
        ? params.slug
        : Array.isArray(params?.slug)
          ? params.slug[0] ?? null
          : null;

    if (!s) {
      return {
        slug: null,
        seo: {
          title: 'AI Bots',
          description: 'Browse Gab AI’s catalog of specialized AI bots.',
          canonical: '/agents',
          keywords: 'AI bots, AI assistants, Gab AI bots',
          structuredData: null,
        },
      };
    }

    const cached = _staticCategoryCache[s];
    const category = cached?.category || null;
    const agents = cached?.agents || [];

    return {
      slug: s,
      seo: {
        title: buildCategoryTitle(category),
        description: buildCategoryDescription(category),
        canonical: `/agents/category/${s}`,
        keywords: buildKeywords(category),
        structuredData: buildStructuredData(category, agents, s),
      },
    };
  }, [params?.slug]);

  return (
    <>
      <MetaHead
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        keywords={seo.keywords}
        structuredData={seo.structuredData}
      />
      <AgentsPage categorySlug={slug || undefined} />
    </>
  );
}
